import { Button } from '@mantine/core';
import React from 'react';

import {
  StoreListing,
  useCreateStoreListing,
  usePublishStoreListing,
  useStoreSettings,
  useUpdateStoreListingPrices,
} from '@portals/api/partners';

import { mapPaymentSettingsToStoreListingPrices } from '../../../utils/store-listings.utils';
import { useStoreListingWizard, useValidateBeforePublish } from '../hooks';
import { OnCloseCallback } from '../types';

interface PublishButtonProps {
  onClose: OnCloseCallback;
}

export function PublishButton({ onClose }: PublishButtonProps) {
  const { contextData, setContextData } = useStoreListingWizard();
  const { validatePaymentSettings, validatePublishPrerequisites } =
    useValidateBeforePublish();

  const storeSettings = useStoreSettings();
  const createStoreListing = useCreateStoreListing();
  const updateStoreListingPrices = useUpdateStoreListingPrices();
  const publishStoreListing = usePublishStoreListing();

  const isLoading =
    publishStoreListing.isLoading ||
    updateStoreListingPrices.isLoading ||
    createStoreListing.isLoading;

  const getButtonLabel = () => {
    if (contextData.storeListing?.status === 'published') {
      return 'Update';
    }

    return 'Publish to Store';
  };

  const createOrUpdateStoreListing = async () => {
    if (!storeSettings.data || !contextData.selectedProduct) {
      return;
    }

    const prices = mapPaymentSettingsToStoreListingPrices({
      pricingModel: contextData.selectedProduct.pricing_model,
      currencies: storeSettings.data.currencies,
      paymentSettings: contextData.paymentSettings,
    });

    // determine if we need to create or update the store listing
    if (contextData.storeListing) {
      // update the store listing
      return await updateStoreListingPrices.mutateAsync({
        storeListingId: contextData.storeListing.id,
        prices,
      });
    }

    // create the store listing
    return await createStoreListing.mutateAsync({
      productId: contextData.selectedProduct.id,
      prices,
    });
  };

  const publish = async (storeListing: StoreListing) => {
    try {
      await publishStoreListing.mutateAsync({
        storeListingId: storeListing.id,
      });

      onClose({ confirmBeforeClose: false });
    } catch (e) {
      console.error(e);
    }
  };

  const onClick = async () => {
    if (!contextData.selectedProduct || !storeSettings.data) return;

    if (!validatePaymentSettings()) return;

    try {
      const storeListing = await createOrUpdateStoreListing();

      if (!storeListing) return;

      setContextData((prev) => ({
        ...prev,
        storeListing,
        selectedProduct: storeListing.product,
      }));

      validatePublishPrerequisites({
        storeListing,
        onFulfillment: () => publish(storeListing),
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Button
      loading={isLoading}
      onClick={onClick}
      data-testid="modal-publish-btn"
    >
      {getButtonLabel()}
    </Button>
  );
}
